<script>
    import { onMount } from 'svelte';
    import Layout from '../components/Layout.svelte';

    let invoicesToSync = [];
    let currentIndex = 0;
    let syncComplete = false;
    let error = null;
    let syncResults = [];

    onMount(async () => {
        // Get invoices from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        
        // First try to get data from the server-side storage
        const storageId = urlParams.get('storageId');
        if (storageId) {
            try {
                console.log('Fetching invoices data from storage with ID:', storageId);
                const response = await fetch(`/api/temp-storage/${storageId}`);
                
                if (!response.ok) {
                    throw new Error('Failed to retrieve invoice data. Session may have expired.');
                }
                
                invoicesToSync = await response.json();
                console.log('Successfully retrieved', invoicesToSync.length, 'invoices from storage');
                
                // Start sync process
                startSync();
                
                // Clean up storage when we're done
                setTimeout(() => {
                    fetch(`/api/temp-storage/${storageId}`, { method: 'DELETE' })
                        .catch(err => console.error('Error cleaning up storage:', err));
                }, 5 * 60 * 1000); // Clean up after 5 minutes
                
            } catch (err) {
                error = err.message;
                console.error('Error fetching from storage:', err);
            }
        } else {
            // Fallback to old method (URL parameter) for backward compatibility
            const invoicesParam = urlParams.get('invoices');
            if (invoicesParam) {
                try {
                    console.log('Using legacy URL parameter method');
                    invoicesToSync = JSON.parse(decodeURIComponent(invoicesParam));
                    startSync();
                } catch (err) {
                    error = 'Failed to parse invoice data';
                    console.error(err);
                }
            } else {
                error = 'No invoices provided for sync';
            }
        }
    });

    async function startSync() {
        for (let i = 0; i < invoicesToSync.length; i++) {
            currentIndex = i;
            const invoice = invoicesToSync[i];
            
            try {
                console.log(`Syncing invoice ${i + 1}/${invoicesToSync.length}: ${invoice.number}`);
                
                const response = await fetch('/api/xero/create-invoice', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(invoice)
                });

                if (!response.ok) {
                    let errorMessage = 'Failed to sync invoice';
                    let duplicateNumber = false;
                    try {
                        const errorData = await response.json();
                        duplicateNumber = errorData.duplicateNumber === true;
                        console.log('📥 [CLIENT] Received error data:', errorData);
                        
                        if (errorData.message) {
                            errorMessage = errorData.message;
                            console.log('📥 [CLIENT] Using message field:', errorMessage);
                        } else if (errorData.error) {
                            errorMessage = errorData.error;
                            console.log('📥 [CLIENT] Using error field:', errorMessage);
                        }
                    } catch (parseError) {
                        // If JSON parsing fails, fall back to text
                        console.log('📥 [CLIENT] JSON parse failed, using text response');
                        errorMessage = await response.text();
                    }
                    throw Object.assign(new Error(errorMessage), { duplicateNumber });
                }

                const result = await response.json();
                syncResults.push({
                    invoice,
                    success: true,
                    invoiceNumber: result.invoiceNumber,
                    invoiceID: result.invoiceID,
                    xeroUrl: result.xeroUrl,
                    warning: result.warning
                });
                
                // Force UI update
                syncResults = [...syncResults];
                
            } catch (err) {
                console.error(`Error syncing invoice ${invoice.number}:`, err);
                syncResults.push({
                    invoice,
                    success: false,
                    error: err.message,
                    // Xero already has this number: offer "Create anyway" (server names it -a).
                    duplicateNumber: err.duplicateNumber === true
                });
                // Force UI update
                syncResults = [...syncResults];
            }

            // Add a small delay between requests to avoid overwhelming the API
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        syncComplete = true;
    }

    // The user has checked the Xero invoice with the same number and says this
    // one is genuinely separate: create it anyway (numbered "-a" by the server).
    async function createAnyway(result) {
        result.creating = true;
        syncResults = [...syncResults];
        try {
            const response = await fetch('/api/xero/create-invoice', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...result.invoice, allowDuplicateNumber: true })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.message || data.error || 'Failed to sync invoice');
            Object.assign(result, {
                success: true,
                error: null,
                duplicateNumber: false,
                invoiceNumber: data.invoiceNumber,
                invoiceID: data.invoiceID,
                xeroUrl: data.xeroUrl,
                warning: data.warning
            });
        } catch (err) {
            result.error = err.message;
        } finally {
            result.creating = false;
            syncResults = [...syncResults];
        }
    }

    function handleBackClick() {
        window.location.href = '/sync-invoices';
    }
</script>

<Layout>
    <div class="p-6">
        <h1 class="text-2xl font-semibold mb-6">Sync Progress</h1>

        {#if error}
            <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
                <button 
                    class="ml-4 underline"
                    on:click={handleBackClick}
                >
                    Go Back
                </button>
            </div>
        {:else}
            <div class="mb-6">
                <div class="text-lg mb-2">
                    Progress: {currentIndex + 1} of {invoicesToSync.length} Invoices
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                        class="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                        style="width: {((currentIndex + 1) / invoicesToSync.length) * 100}%"
                    ></div>
                </div>
            </div>

            <div class="space-y-4">
                {#each syncResults as result}
                    <div class="border rounded-lg p-4 {result.success ? 'bg-green-50' : 'bg-red-50'}">
                        <div class="font-medium">
                            Invoice #{result.invoice.number}
                            {#if result.success}
                                <span class="text-green-600 ml-2">✓</span>
                            {:else}
                                <span class="text-red-600 ml-2">✗</span>
                            {/if}
                        </div>
                        <div class="text-sm text-gray-600">
                            Customer: {result.invoice.customer}
                        </div>
                            {#if result.success}
                                <div class="text-sm text-green-600 flex items-center">
                                    <span>Created in Xero: {result.invoiceNumber}</span>
                                    {#if result.xeroUrl}
                                        <a 
                                            href={result.xeroUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            class="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded transition-colors duration-200 ml-3"
                                        >
                                            Open in Xero →
                                        </a>
                                    {/if}
                                </div>
                                {#if (result.invoice?.total > 0) && ((result.invoice?.amountPaid || 0) >= result.invoice?.total)}
                                    <div class="text-sm text-amber-700 mt-1 bg-amber-50 px-2 py-1 rounded">
                                        Note: This PAID invoice is synced to Xero as UNPAID. Please add payment to the Xero invoice within Xero.
                                    </div>
                                {/if}
                                {#if result.warning}
                                    <div class="text-sm text-red-600 mt-1">
                                        Warning: {result.warning}
                                    </div>
                                {/if}
                        {:else}
                            <div class="text-sm text-red-600">
                                Error: {result.error}
                            </div>
                            {#if result.duplicateNumber}
                                <button
                                    class="mt-2 bg-white border border-red-300 text-red-700 hover:bg-red-100 text-xs px-3 py-1 rounded disabled:opacity-50"
                                    disabled={result.creating}
                                    on:click={() => createAnyway(result)}
                                >
                                    {result.creating ? 'Creating…' : 'Create anyway'}
                                </button>
                            {/if}
                        {/if}
                    </div>
                {/each}
            </div>

            {#if syncComplete}
                <div class="mt-6 flex justify-center">
                    <button 
                        class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        on:click={handleBackClick}
                    >
                        Back to Invoices
                    </button>
                </div>
            {/if}
        {/if}
    </div>
</Layout> 